import { getStatusDefinition, TICKET_PRIORITIES } from '../constants/ticket';
import { getTicketAssignees } from './ticketAssignees';

// استيراد التذاكر من ملف Excel (نفس شكل ملف التصدير في /reports):
// - صف فيه رقم تذكرة  => تعديل التذكرة دي (الخلايا الفاضية = من غير تغيير).
// - صف من غير رقم تذكرة => إنشاء تذكرة جديدة.

// تطبيع النصوص للمقارنة: حروف صغيرة، توحيد الألف/الياء/التاء المربوطة، وشيل التشكيل والمسافات الزايدة.
export const normalizeText = (value) => String(value ?? '')
    .toLowerCase()
    .replace(/[ً-ْـ]/g, '')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/[_\-:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const HEADER_ALIASES = {
    ticketNumber: ['رقم التذكرة', 'ticket #', 'ticket number', 'ticket no', 'ticketnumber'],
    subject: ['عنوان التذكرة', 'العنوان', 'subject', 'title'],
    content: ['الوصف', 'المحتوى', 'تفاصيل التذكرة', 'description', 'content', 'details'],
    tenant: ['العميل', 'customer', 'tenant'],
    assignee: ['معينة لـ', 'معينة ل', 'المعين', 'assigned to', 'assignee', 'assignees'],
    section: ['القسم', 'section'],
    product: ['المنتج / الخدمة', 'المنتج', 'الخدمة', 'product'],
    priority: ['الأولوية', 'priority'],
    visibility: ['نوع التذكرة', 'ticket type', 'visibility', 'type'],
    status: ['الحالة', 'status'],
    expectedAt: ['الوقت المتوقع', 'expected time', 'expected at'],
};

const HEADER_LOOKUP = Object.entries(HEADER_ALIASES).reduce((acc, [key, aliases]) => {
    aliases.forEach((alias) => { acc[normalizeText(alias)] = key; });
    return acc;
}, {});

// قيمة الخلية من exceljs ممكن تكون نص/رقم/تاريخ/rich text/hyperlink/formula.
const cellToValue = (value) => {
    if (value == null) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'object') {
        if (Array.isArray(value.richText)) return value.richText.map((part) => part.text || '').join('');
        if ('result' in value) return cellToValue(value.result);
        if ('text' in value) return cellToValue(value.text);
        return null;
    }
    return value;
};

const isBlank = (value) => value == null || String(value).trim() === '';

// ملف التصدير بيبدأ بعنوان وملخص فلاتر — بندوّر على صف العناوين اللي فيه "رقم التذكرة".
const findHeaderRow = (sheet) => {
    const maxScan = Math.min(sheet.rowCount, 60);
    for (let rowIndex = 1; rowIndex <= maxScan; rowIndex += 1) {
        const columns = {};
        sheet.getRow(rowIndex).eachCell({ includeEmpty: false }, (cell, colNumber) => {
            const key = HEADER_LOOKUP[normalizeText(cellToValue(cell.value))];
            if (key && !(key in columns)) columns[key] = colNumber;
        });
        if ('ticketNumber' in columns && Object.keys(columns).length >= 2) {
            return { rowIndex, columns };
        }
    }
    return null;
};

export const readTicketImportFile = async (file) => {
    const { default: ExcelJS } = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const sheet = workbook.worksheets[0];
    if (!sheet) throw new Error('NO_SHEET');
    const header = findHeaderRow(sheet);
    if (!header) throw new Error('NO_HEADER');

    const rows = [];
    for (let rowIndex = header.rowIndex + 1; rowIndex <= sheet.rowCount; rowIndex += 1) {
        const excelRow = sheet.getRow(rowIndex);
        const values = {};
        Object.entries(header.columns).forEach(([key, colNumber]) => {
            const value = cellToValue(excelRow.getCell(colNumber).value);
            values[key] = typeof value === 'string' ? value.trim() : value;
        });
        if (Object.values(values).every(isBlank)) continue;
        rows.push({ rowNumber: rowIndex, values });
    }
    return { columns: Object.keys(header.columns), rows };
};

// --- تحويل قيم الملف لقيم النظام ---

const matchCatalogItem = (items, value) => {
    const target = normalizeText(value);
    return (items || []).find((item) =>
        [item.key, item.labelAr, item.labelEn].some((candidate) => normalizeText(candidate) === target)
    ) || null;
};

const matchStatus = (statuses, value) => {
    const target = normalizeText(value);
    return (statuses || []).find((item) =>
        [item._id, item.labelAr, item.labelEn].some((candidate) => normalizeText(candidate) === target)
    ) || null;
};

const matchPriority = (value) => {
    const target = normalizeText(value);
    return TICKET_PRIORITIES.find((item) =>
        [item.value, item.labelAr, item.labelEn].some((candidate) => {
            const normalized = normalizeText(candidate);
            // يقبل "عالية" أو "High" أو "P2" من غير باقي النص
            return normalized === target || normalized.replace(/\s*\(p\d\)$/, '') === target || normalized.endsWith(`(${target})`);
        })
    ) || null;
};

const matchVisibility = (value) => {
    const target = normalizeText(value);
    if (['internal', 'داخلي'].includes(target)) return 'internal';
    if (['external', 'خارجي'].includes(target)) return 'external';
    return null;
};

export const getTenantLabel = (tenant) => tenant?.tenantName || tenant?.username || tenant?._id || '';

const matchTenant = (tenants, value) => {
    const target = normalizeText(value);
    return (tenants || []).find((tenant) =>
        [tenant?._id, tenant?.tenantName, tenant?.username].some((candidate) => candidate && normalizeText(candidate) === target)
    ) || null;
};

const matchAdmin = (admins, value) => {
    const target = normalizeText(value);
    return (admins || []).find((admin) =>
        [admin?._id, admin?.name, admin?.username, admin?.email].some((candidate) => candidate && normalizeText(candidate) === target)
    ) || null;
};

// التاريخ في ملف التصدير متكتب بالتوقيت المحلي على إنه UTC (شوف toExcelDate في Reports) — بنرجّعه زي ما هو.
const parseImportDate = (value) => {
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return null;
        const local = new Date(value.getTime() + value.getTimezoneOffset() * 60000);
        return new Date(value.getTime() + local.getTimezoneOffset() * 60000);
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        // رقم تسلسلي بتاع Excel
        return parseImportDate(new Date(Math.round((value - 25569) * 86400000)));
    }
    const raw = String(value || '').trim();
    if (!raw || raw === '-') return null;
    const isoLike = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/);
    if (isoLike) {
        const [, y, m, d, hh = '0', mm = '0'] = isoLike;
        return new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm));
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const sameMinute = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return Math.floor(new Date(a).getTime() / 60000) === Math.floor(new Date(b).getTime() / 60000);
};

const escapeHtml = (value) => String(value || '').replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

const buildTenantFields = (tenant) => {
    const company = tenant?.companies?.[0];
    const branch = company?.branches?.[0];
    const owner = tenant?.ownerUser || null;
    return {
        tenantId: tenant?._id || null,
        tenantName: getTenantLabel(tenant),
        customerName: owner?.name || tenant?.contactPersonName || getTenantLabel(tenant) || null,
        customerId: owner?._id || tenant?.userId || tenant?._id || null,
        email: owner?.email || company?.mainEmail || '',
        mobile: owner?.mobile || tenant?.contactPersonMobile || tenant?.tenantPhone || company?.mainPhone || '',
        companyId: company?._id || null,
        branchId: branch?._id || null,
    };
};

// القيم اللي ملف التصدير بيكتبها للتذكرة دي دلوقتي (بأي لغة). لو الخلية زيها يبقى المستخدم ماغيّرهاش،
// فمانحاولش نطابقها — عشان عميل اسمه اتغيّر أو موظف مش في القائمة مايوقفش الصف كله.
const currentCellValues = (ticket, lookups) => {
    const catalogNames = (items, key) => {
        const item = (items || []).find((entry) => entry.key === key);
        return [key, item?.labelAr, item?.labelEn];
    };
    const status = getStatusDefinition(lookups.statuses, ticket?.statusId || ticket?.status);
    const tenantRecord = (lookups.tenants || []).find((tenant) => String(tenant?._id) === String(ticket?.tenantId));
    const names = getTicketAssignees(ticket).map((entry) => {
        const admin = (lookups.admins || []).find((item) => String(item?._id) === String(entry.userId));
        return entry.name || admin?.name || admin?.username || entry.userId;
    });
    const toSet = (list) => new Set(list.filter((value) => !isBlank(value)).map(normalizeText));
    return {
        tenant: toSet([ticket?.tenantName, ticket?.tenantId, getTenantLabel(tenantRecord)]),
        section: toSet(catalogNames(lookups.sections, ticket?.sectionId)),
        product: toSet(catalogNames(lookups.products, ticket?.ProductId)),
        status: toSet([ticket?.statusId, ticket?.status, status?._id, status?.labelAr, status?.labelEn]),
        assignee: names.map(normalizeText).sort().join('|'),
    };
};

const isUnchangedCell = (key, value, current) => {
    if (!current) return false;
    if (key === 'assignee') {
        const names = String(value).split(/[،,;]/).map((name) => normalizeText(name)).filter(Boolean).sort();
        return names.join('|') === current.assignee;
    }
    return current[key] instanceof Set && current[key].has(normalizeText(value));
};

// بيحوّل خلايا الصف لقيم النظام. الخلايا الفاضية بتتشال (في التعديل = من غير تغيير).
// ticket = التذكرة الحالية في حالة التعديل: الخلايا اللي قيمتها زي التذكرة بتتساب من غير تحقق.
const resolveRowValues = (values, lookups, t, ticket = null) => {
    const resolved = {};
    const errors = [];
    const current = ticket ? currentCellValues(ticket, lookups) : null;
    const has = (key) => key in values && !isBlank(values[key]) && !isUnchangedCell(key, values[key], current);

    if (has('subject')) resolved.subject = String(values.subject).trim();
    if (has('content')) resolved.content = String(values.content).trim();

    if (has('section')) {
        const item = matchCatalogItem(lookups.sections, values.section);
        if (item) resolved.sectionId = item.key;
        else errors.push(t('unknownSection', values.section));
    }
    if (has('product')) {
        const item = matchCatalogItem(lookups.products, values.product);
        if (item) resolved.ProductId = item.key;
        else errors.push(t('unknownProduct', values.product));
    }
    if (has('priority')) {
        const item = matchPriority(values.priority);
        if (item) resolved.priority = item.value;
        else errors.push(t('unknownPriority', values.priority));
    }
    if (has('visibility')) {
        const visibility = matchVisibility(values.visibility);
        if (visibility) resolved.visibility = visibility;
        else errors.push(t('unknownVisibility', values.visibility));
    }
    if (has('status')) {
        const item = matchStatus(lookups.statuses, values.status);
        if (item) resolved.statusId = String(item._id);
        else errors.push(t('unknownStatus', values.status));
    }
    if (has('tenant')) {
        const tenant = matchTenant(lookups.tenants, values.tenant);
        if (tenant) resolved.tenant = tenant;
        else errors.push(t('unknownTenant', values.tenant));
    }
    if (has('expectedAt')) {
        const date = parseImportDate(values.expectedAt);
        if (date) resolved.expectedAt = date;
        else errors.push(t('invalidDate', values.expectedAt));
    }
    if (has('assignee') && lookups.canAssign) {
        const names = String(values.assignee).split(/[،,;]/).map((name) => name.trim()).filter(Boolean);
        const admins = [];
        names.forEach((name) => {
            const admin = matchAdmin(lookups.admins, name);
            if (admin) {
                if (!admins.some((item) => String(item._id) === String(admin._id))) admins.push(admin);
            } else {
                errors.push(t('unknownAssignee', name));
            }
        });
        resolved.assignees = admins;
    }
    return { resolved, errors };
};

const assigneesPayload = (admins) => ({
    assignees: admins.map((admin) => ({
        userId: String(admin._id),
        name: String(admin.name || admin.username || '').trim(),
        email: String(admin.email || '').trim(),
    })),
});

const catalogLabel = (items, key, lang) => {
    const item = (items || []).find((entry) => entry.key === key);
    return item ? (lang === 'ar' ? item.labelAr : item.labelEn) : (key || '-');
};

// يقارن الصف بالتذكرة الحالية ويرجّع التغييرات + الطلبات اللي هتتبعت.
export const buildUpdatePlan = (ticket, resolved, lookups) => {
    const { lang } = lookups;
    const changes = [];
    const body = {};
    const label = (ar, en) => (lang === 'ar' ? ar : en);

    if (resolved.subject !== undefined && resolved.subject !== String(ticket?.subject || '').trim()) {
        body.subject = resolved.subject;
        changes.push({ field: label('العنوان', 'Subject'), from: ticket?.subject || '-', to: resolved.subject });
    }
    if (resolved.sectionId !== undefined && resolved.sectionId !== ticket?.sectionId) {
        body.sectionId = resolved.sectionId;
        changes.push({ field: label('القسم', 'Section'), from: catalogLabel(lookups.sections, ticket?.sectionId, lang), to: catalogLabel(lookups.sections, resolved.sectionId, lang) });
    }
    if (resolved.ProductId !== undefined && resolved.ProductId !== ticket?.ProductId) {
        body.ProductId = resolved.ProductId;
        changes.push({ field: label('المنتج', 'Product'), from: catalogLabel(lookups.products, ticket?.ProductId, lang), to: catalogLabel(lookups.products, resolved.ProductId, lang) });
    }
    const currentPriority = String(ticket?.priority || 'medium').toLowerCase();
    if (resolved.priority !== undefined && resolved.priority !== currentPriority) {
        body.priority = resolved.priority;
        const priorityLabel = (value) => {
            const item = TICKET_PRIORITIES.find((entry) => entry.value === value);
            return item ? (lang === 'ar' ? item.labelAr : item.labelEn) : value;
        };
        changes.push({ field: label('الأولوية', 'Priority'), from: priorityLabel(currentPriority), to: priorityLabel(resolved.priority) });
    }
    const currentVisibility = String(ticket?.visibility || 'external').toLowerCase() === 'internal' ? 'internal' : 'external';
    if (resolved.visibility !== undefined && resolved.visibility !== currentVisibility) {
        body.visibility = resolved.visibility;
        const visibilityLabel = (value) => (value === 'internal' ? label('داخلي', 'Internal') : label('خارجي', 'External'));
        changes.push({ field: label('نوع التذكرة', 'Ticket type'), from: visibilityLabel(currentVisibility), to: visibilityLabel(resolved.visibility) });
    }
    if (resolved.expectedAt !== undefined && !sameMinute(resolved.expectedAt, ticket?.expectedAt)) {
        body.expectedAt = resolved.expectedAt.toISOString();
        const fmt = (value) => (value ? new Date(value).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '-');
        changes.push({ field: label('الوقت المتوقع', 'Expected time'), from: fmt(ticket?.expectedAt), to: fmt(resolved.expectedAt) });
    }
    if (resolved.tenant !== undefined && String(resolved.tenant._id) !== String(ticket?.tenantId || '')) {
        Object.assign(body, buildTenantFields(resolved.tenant));
        changes.push({ field: label('العميل', 'Customer'), from: ticket?.tenantName || ticket?.tenantId || '-', to: getTenantLabel(resolved.tenant) });
    }
    if (resolved.assignees !== undefined) {
        const currentIds = getTicketAssignees(ticket).map((entry) => String(entry.userId)).sort();
        const nextIds = resolved.assignees.map((admin) => String(admin._id)).sort();
        if (currentIds.join('|') !== nextIds.join('|')) {
            Object.assign(body, assigneesPayload(resolved.assignees));
            changes.push({
                field: label('معينة لـ', 'Assigned to'),
                from: getTicketAssignees(ticket).map((entry) => entry.name || entry.userId).join('، ') || '-',
                to: resolved.assignees.map((admin) => admin.name || admin.username).join('، ') || '-',
            });
        }
    }

    let statusId = null;
    if (resolved.statusId !== undefined) {
        const current = getStatusDefinition(lookups.statuses, ticket?.statusId || ticket?.status);
        if (String(current?._id || '') !== resolved.statusId) {
            statusId = resolved.statusId;
            const next = lookups.statuses.find((item) => String(item._id) === resolved.statusId);
            changes.push({
                field: label('الحالة', 'Status'),
                from: current ? (lang === 'ar' ? current.labelAr : current.labelEn) : '-',
                to: next ? (lang === 'ar' ? next.labelAr : next.labelEn) : resolved.statusId,
            });
        }
    }

    return { changes, body: Object.keys(body).length ? body : null, statusId };
};

export const buildCreatePlan = (resolved, lookups, t) => {
    const errors = [];
    if (!resolved.subject) errors.push(t('requiredSubject'));
    if (!resolved.tenant) errors.push(t('requiredTenant'));
    if (!resolved.sectionId) errors.push(t('requiredSection'));
    if (!resolved.ProductId) errors.push(t('requiredProduct'));
    if (errors.length) return { errors };

    const content = resolved.content || resolved.subject;
    const statusId = resolved.statusId || (lookups.statuses.find((item) => item.isDefault) || lookups.statuses[0])?._id || '';
    const body = {
        ...buildTenantFields(resolved.tenant),
        subject: resolved.subject,
        content: content.split(/\r?\n/).map((line) => `<p>${escapeHtml(line) || '<br>'}</p>`).join(''),
        sectionId: resolved.sectionId,
        ProductId: resolved.ProductId,
        priority: resolved.priority || 'medium',
        visibility: resolved.visibility || 'external',
        statusId,
        createdBy: lookups.userId || null,
        ...(resolved.expectedAt ? { expectedAt: resolved.expectedAt.toISOString() } : {}),
        ...(resolved.assignees?.length ? assigneesPayload(resolved.assignees) : {}),
    };
    return { body, errors: [] };
};

// يحلل كل الصفوف: يجيب التذكرة الحالية برقمها ويحسب الفرق، أو يجهّز إنشاء تذكرة جديدة.
export const analyzeImportRows = async (rows, lookups, { findTicketByNumber, t, onProgress }) => {
    const seenNumbers = new Set();
    const results = new Array(rows.length);
    let done = 0;

    await runPool(rows, 5, async (row, index) => {
        const rawNumber = row.values.ticketNumber;
        const base = { rowNumber: row.rowNumber, subject: isBlank(row.values.subject) ? '' : String(row.values.subject).trim() };

        if (isBlank(rawNumber)) {
            const { resolved, errors } = resolveRowValues(row.values, lookups, t);
            const plan = buildCreatePlan(resolved, lookups, t);
            const allErrors = [...errors, ...plan.errors];
            results[index] = allErrors.length
                ? { ...base, action: 'error', errors: allErrors }
                : { ...base, action: 'create', body: plan.body, changes: [] };
        } else {
            const ticketNumber = Number(String(rawNumber).trim());
            if (!Number.isInteger(ticketNumber) || ticketNumber <= 0) {
                results[index] = { ...base, action: 'error', errors: [t('invalidTicketNumber', rawNumber)] };
            } else if (seenNumbers.has(ticketNumber)) {
                results[index] = { ...base, ticketNumber, action: 'error', errors: [t('duplicateTicketNumber', ticketNumber)] };
            } else {
                seenNumbers.add(ticketNumber);
                let ticket = null;
                try {
                    ticket = await findTicketByNumber(ticketNumber);
                } catch {
                    ticket = null;
                }
                const { resolved, errors } = ticket ? resolveRowValues(row.values, lookups, t, ticket) : { resolved: {}, errors: [] };
                if (!ticket) {
                    results[index] = { ...base, ticketNumber, action: 'error', errors: [t('ticketNotFound', ticketNumber)] };
                } else if (errors.length) {
                    results[index] = { ...base, ticketNumber, subject: resolved.subject || ticket.subject, action: 'error', errors };
                } else {
                    const plan = buildUpdatePlan(ticket, resolved, lookups);
                    results[index] = {
                        ...base,
                        ticketNumber,
                        subject: resolved.subject || ticket.subject,
                        ticketId: ticket._id,
                        action: plan.changes.length ? 'update' : 'unchanged',
                        changes: plan.changes,
                        body: plan.body,
                        statusId: plan.statusId,
                    };
                }
            }
        }
        done += 1;
        onProgress?.(done, rows.length);
    });

    return results;
};

export const runPool = async (items, limit, worker) => {
    let cursor = 0;
    const next = async () => {
        while (cursor < items.length) {
            const index = cursor;
            cursor += 1;
            await worker(items[index], index);
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, next));
};
